import { ArrowSquareOutIcon } from '@phosphor-icons/react'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePixelToast } from '../components/ui/pixel-toast'
import { displayDiagEnabled, noteDisplayEvent } from '../lib/displayWriteDiag'
import { txHashToTonviewer } from '../lib/tonviewer'

const ELAPSED_TICK_MS = 100
const TERMINAL_TOAST_DURATION_MS = 7_000

export type TradeLifecycleToastStart = {
  label: string
  content?: ReactNode
}

export type TradeLifecycleToastStage = {
  content?: ReactNode
}

export type TradeLifecycleToastSubmitted = TradeLifecycleToastStage & {
  /** Submitted-message hash. It is deliberately not treated as a confirmed tx link. */
  submittedHash?: string | null
}

export type TradeLifecycleToastConfirmed = TradeLifecycleToastStage & {
  /** Finalized on-chain transaction hash used by the existing Tonviewer treatment. */
  txHash: string
}

export type TradeLifecycleToastFailure = {
  /** Caller-provided, already-safe user-facing reason. It is rendered without rewriting. */
  reason: string
  content?: ReactNode
  /** Present only when the failure itself has a finalized on-chain transaction. */
  txHash?: string | null
}

export type TradeLifecycleToastHandle = {
  /** Quote/preflight/wallet-wait progress. Remains pending. */
  pending: (stage?: TradeLifecycleToastStage) => boolean
  /** Local signing progress. Remains pending. */
  signing: (stage?: TradeLifecycleToastStage) => boolean
  /** Local relay acceptance. Remains pending until a finalized chain event. */
  submitted: (stage?: TradeLifecycleToastSubmitted) => boolean
  /** First terminal transition wins. Preserves elapsed time and adds the tx link. */
  confirmed: (result: TradeLifecycleToastConfirmed) => boolean
  /** Local success with no chain tx (limit book). */
  ok: (stage?: TradeLifecycleToastStage) => boolean
  /** First terminal transition wins. Renders the exact safe reason. */
  failed: (failure: TradeLifecycleToastFailure) => boolean
  dismiss: () => boolean
  /** Drop hook ownership so the toast survives the starter unmounting (list Buy). */
  release: () => void
}

export type TradeLifecycleToastApi = {
  /** Call synchronously in the click handler, before quote/signing work starts. */
  start: (input: TradeLifecycleToastStart) => TradeLifecycleToastHandle
}

type ActiveTradeToast = {
  id: number
  label: string
  startedAtMS: number
  terminal: boolean
  dismissed: boolean
}

/**
 * Owns the lifecycle of one existing production PixelToast per returned handle.
 *
 * The handle is also the stale-work guard: quote/preflight callbacks from an old
 * run cannot update another run, and no non-terminal callback can overwrite the
 * first terminal result.
 */
export function useTradeLifecycleToast(): TradeLifecycleToastApi {
  const { push, update, dismiss } = usePixelToast()
  const active = useRef(new Set<ActiveTradeToast>())

  useEffect(
    () => () => {
      for (const toast of active.current) dismiss(toast.id)
      active.current.clear()
    },
    [dismiss]
  )

  const start = useCallback(
    ({ label, content }: TradeLifecycleToastStart): TradeLifecycleToastHandle => {
      const startedAtMS = Date.now()
      const toast: ActiveTradeToast = {
        id: 0,
        label,
        startedAtMS,
        terminal: false,
        dismissed: false,
      }
      toast.id = push({
        tone: 'pending',
        content: content ?? `Preparing ${label}…`,
        tail: <TradeToastTail startedAtMS={startedAtMS} />,
      })
      active.current.add(toast)
      if (displayDiagEnabled())
        noteDisplayEvent('toast', `#${toast.id}`, 'start-pending', label, 'click')

      const updatePending = (nextContent: ReactNode): boolean => {
        if (toast.terminal || toast.dismissed) return false
        update(toast.id, {
          tone: 'pending',
          content: nextContent,
          tail: <TradeToastTail startedAtMS={startedAtMS} />,
          duration: 0,
          onClick: undefined,
        })
        return true
      }

      const finish = (
        tone: 'ok' | 'fail',
        terminalContent: ReactNode,
        txHash?: string | null,
        elapsed = true
      ): boolean => {
        if (toast.terminal || toast.dismissed) return false
        toast.terminal = true
        active.current.delete(toast)
        if (displayDiagEnabled())
          noteDisplayEvent(
            'toast',
            `#${toast.id}`,
            `terminal-${tone}`,
            toast.label,
            'confirmation/failure'
          )
        const finishedAtMS = Date.now()
        const explorerURL = txHash ? txHashToTonviewer(txHash) : null
        update(toast.id, {
          tone,
          content: terminalContent,
          tail: tradeToastTail({
            startedAtMS,
            finishedAtMS,
            explorer: explorerURL !== null,
            elapsed,
          }),
          duration: TERMINAL_TOAST_DURATION_MS,
          onClick: explorerURL
            ? () => window.open(explorerURL, '_blank', 'noopener,noreferrer')
            : undefined,
        })
        return true
      }

      return {
        pending: (stage = {}) => updatePending(stage.content ?? `Preparing ${label}…`),
        signing: (stage = {}) => updatePending(stage.content ?? `Signing ${label}…`),
        submitted: (stage = {}) => {
          // Keep the submitted hash bound to this handle, but do not present it
          // as a finalized transaction or success.
          void stage.submittedHash
          return updatePending(stage.content ?? `${label} submitted · waiting for chain`)
        },
        confirmed: ({ txHash, content: confirmedContent }) =>
          finish('ok', confirmedContent ?? `${label} confirmed`, txHash),
        ok: (stage = {}) => finish('ok', stage.content ?? `${label} · open`, null, false),
        failed: ({ reason, txHash, content: failedContent }) =>
          finish('fail', failedContent ?? `${label} failed · ${reason}`, txHash),
        dismiss: () => {
          if (toast.terminal || toast.dismissed) return false
          toast.dismissed = true
          active.current.delete(toast)
          if (displayDiagEnabled())
            noteDisplayEvent('toast', `#${toast.id}`, 'dismissed', toast.label, 'dismiss')
          dismiss(toast.id)
          return true
        },
        release: () => {
          active.current.delete(toast)
        },
      }
    },
    [dismiss, push, update]
  )

  return useMemo(() => ({ start }), [start])
}

function tradeToastTail(props: {
  startedAtMS: number
  finishedAtMS: number
  explorer: boolean
  elapsed: boolean
}) {
  if (!props.elapsed && !props.explorer) return undefined
  return <TradeToastTail {...props} />
}

function TradeToastTail({
  startedAtMS,
  finishedAtMS,
  explorer = false,
  elapsed = true,
}: {
  startedAtMS: number
  finishedAtMS?: number
  explorer?: boolean
  elapsed?: boolean
}) {
  const [nowMS, setNowMS] = useState(() => Date.now())

  useEffect(() => {
    if (!elapsed || finishedAtMS !== undefined) return
    const timer = window.setInterval(() => setNowMS(Date.now()), ELAPSED_TICK_MS)
    return () => window.clearInterval(timer)
  }, [elapsed, finishedAtMS])

  const elapsedMS = Math.max(0, (finishedAtMS ?? nowMS) - startedAtMS)
  return (
    <>
      {elapsed ? (
        <span aria-label="Trade elapsed time">{formatTradeToastElapsed(elapsedMS)}</span>
      ) : null}
      {explorer ? (
        <span aria-label="Open transaction in explorer">
          <ArrowSquareOutIcon size={15} weight="bold" aria-hidden="true" />
        </span>
      ) : null}
    </>
  )
}

export function formatTradeToastElapsed(elapsedMS: number): string {
  return `${(Math.max(0, elapsedMS) / 1_000).toFixed(1)}s`
}
